import { Dispatch } from "redux";
import {Alert} from "reactstrap";
import React, {useState, useEffect} from "react";
import {Link, useLocation} from "react-router-dom";
import useTypedNavigate from "../hooks/typed-navigate";
import {DEFAULT_AFTER_AUTH, nextFromSearch, withNext} from "../utils/authRedirect";
import { isUserNotConfirmed } from "../utils/authErrors";
import { useSeo } from "../hooks/useSeo";
import type { SignupOutcome } from "../actions/user";
import ConfirmSignup from "./ConfirmSignup";
import GoogleSignInButton from "./GoogleSignInButton";

interface Props {
  auth: AuthenticationState
  loginAction: (username: string, password: string) => (dispatch: Dispatch<AuthenticationAction>) => Promise<string>
  confirmSignupAction: (email: string, code: string, password: string) => Promise<SignupOutcome>
  clearError: () => void
}

export default function LoginPage(props: Props) {
  useSeo({ title: "Log In", path: "/login", noindex: true });
  const { auth, loginAction, confirmSignupAction, clearError } = props
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitted, setSubmitted] = useState(false);
  // An account that signed up but never entered its emailed code can't sign in;
  // collect the code here rather than bouncing the person to another page.
  const [awaitingCode, setAwaitingCode] = useState(false);
  const navigate = useTypedNavigate();
  const location = useLocation();
  const destination = nextFromSearch(location.search) ?? DEFAULT_AFTER_AUTH;

  useEffect(() => {
    clearError();
  }, [clearError]);

  useEffect(() => {
    if (auth.loggedIn) {
      navigate(destination, { replace: true });
    }
  }, [auth.loggedIn, navigate, destination]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target;
    // A previous attempt's error must not read as a verdict on what's typed now.
    if (auth.errorMessage) clearError();
    if (name === "username") setUsername(value);
    if (name === "password") setPassword(value);
  };

  const handleSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      setSubmitted(true);
      if (!username || !password) return
      await loginAction(username, password)
      navigate(destination, { replace: true });
    } catch (error) {
      if (isUserNotConfirmed(error)) {
        clearError();
        setAwaitingCode(true);
      }
      console.error(error)
    }
  };

  if (awaitingCode) {
    return (
      <div className="container">
        <div className="row justify-content-md-center">
          <div className="col-md-auto login-container">
            <ConfirmSignup
              email={username}
              busy={auth.signingUp}
              fromLogin
              onConfirm={async (code) => {
                await confirmSignupAction(username, code, password);
                navigate(destination, { replace: true });
              }}
            />
            <Alert color="danger" isOpen={!!auth.errorMessage}>
              {auth.errorMessage}
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="row justify-content-md-center">
        <div className="col-md-auto login-container">
          <h2>Login</h2>
          <GoogleSignInButton next={destination} />
          <form name="form" onSubmit={handleSubmit}>
            <div
              className={"form-group" + (submitted && !username ? " has-error" : "")}
            >
              <label htmlFor="username">Email *</label>
              <input
                type="email"
                className="form-control"
                id="username"
                name="username"
                value={username}
                onChange={handleChange}
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                required
              />
              {submitted && !username && (
                <div
                  className="help-block "
                  style={{ color: 'red' }}
                >
                  Email is required
                </div>
              )}
            </div>
            <div
              className={"form-group" + (submitted && !password ? " has-error" : "")}
            >
              <label htmlFor="password">Password *</label>
              <input
                type="password"
                className="form-control"
                id="password"
                name="password"
                value={password}
                onChange={handleChange}
                autoComplete="current-password"
                required
              />
              {submitted && !password && (
                <div
                  className="help-block "
                  style={{ color: 'red' }}
                >
                  Password is required
                </div>
              )}
            </div>
            <div className="form-group">
              <button className="btn btn-primary" disabled={auth.loggingIn}>
                {auth.loggingIn ? <><span className="spinner-border spinner-border-sm" role="status" /> Logging in...</> : "Login"}
              </button>
            </div>
          </form>
          <Alert color="danger" isOpen={!!auth.errorMessage}>
            {auth.errorMessage}
            {auth.errorAction === "signup" && (
              <> <Link to={withNext("/signup", location.search)}>Create an account</Link>.</>
            )}
            {auth.errorAction === "reset" && (
              <> <Link to={withNext("/forgot-password", location.search)}>Reset your password</Link>.</>
            )}
          </Alert>
          <p className="mt-3">
            Don't have an account? <Link to={withNext("/signup", location.search)}>Sign up</Link>
          </p>
          <p>
            <Link to={withNext("/forgot-password", location.search)}>Forgot password?</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
